/**
 * CESM OSINT System - Adversary Intelligence Panel
 * Analyse des sources médiatiques adversaires (Russie, Chine, Iran)
 * 
 * Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel © 2026
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, AlertTriangle, Newspaper, Globe, 
  RefreshCw, Eye, Filter, ChevronDown, ChevronUp,
  ExternalLink, Loader2, Radio, Target, Skull,
  FileText, Download, History
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { addGlobalLog } from './GlobalConsole';
import { useDemoMode } from '@/contexts/DemoModeContext';

// Origin flags and colors
const ORIGIN_CONFIG = {
  russia: { 
    label: 'Russie', 
    flag: '🇷🇺', 
    color: 'text-red-400',
    bgColor: 'bg-red-900/30',
    borderColor: 'border-red-500/50'
  },
  china: { 
    label: 'Chine', 
    flag: '🇨🇳', 
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/30',
    borderColor: 'border-yellow-500/50'
  },
  iran: { 
    label: 'Iran', 
    flag: '🇮🇷', 
    color: 'text-green-400',
    bgColor: 'bg-green-900/30',
    borderColor: 'border-green-500/50'
  },
  axis: { 
    label: 'Axe (Hezbollah)', 
    flag: '⚔️', 
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/30',
    borderColor: 'border-purple-500/50'
  }
};

const AdversaryIntelPanel = ({ token }) => {
  const { checkDemoBlock } = useDemoMode();
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [sources, setSources] = useState([]);
  const [articles, setArticles] = useState([]);
  const [byOrigin, setByOrigin] = useState({});
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [narrativeAnalysis, setNarrativeAnalysis] = useState(null);
  const [showSources, setShowSources] = useState(false);
  const [propagandaResults, setPropagandaResults] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);

  // Load sources on mount and when token changes
  useEffect(() => {
    if (token) {
      loadSources();
      loadReportHistory();
    }
  }, [token]);

  const loadSources = async () => {
    try {
      const res = await axios.get(`${API}/news/adversary/sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      if (data.status === 'success' && Array.isArray(data.sources)) {
        setSources(data.sources);
        addGlobalLog('ADVERSARY', `${data.total_sources} sources adversaires configurées`);
      } else {
        console.warn('Adversary sources: unexpected response', data);
        setSources([]);
      }
    } catch (err) {
      console.error('Error loading adversary sources:', err.response?.status, err.message);
      addGlobalLog('ERROR', `Échec chargement sources adversaires: ${err.message}`);
    }
  };

  const loadReportHistory = async () => {
    try {
      const res = await axios.get(`${API}/reports/adversary/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === 'success') {
        setReportHistory(res.data.reports || []);
      }
    } catch (err) {
      console.error('Error loading report history:', err);
    }
  };

  const fetchAdversaryNews = async () => {
    if (checkDemoBlock('Scanner sources adversaires')) return;
    
    setIsLoading(true);
    addGlobalLog('ADVERSARY', 'Démarrage scan sources adversaires...');
    
    try {
      const params = selectedOrigin ? `?origin=${selectedOrigin}` : '';
      const res = await axios.get(`${API}/news/adversary${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.status === 'success') {
        setArticles(res.data.articles || []);
        setByOrigin(res.data.by_origin || {});
        addGlobalLog('ADVERSARY', `${res.data.count} articles récupérés`);
        toast.success(`${res.data.count} articles adversaires récupérés`);
      }
    } catch (err) {
      console.error('Error fetching adversary news:', err);
      toast.error('Erreur lors du scan adversaire');
      addGlobalLog('ERROR', 'Échec scan adversaire');
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeNarratives = async () => {
    if (checkDemoBlock('Analyse des narratifs adversaires')) return;
    
    if (articles.length === 0) {
      toast.warning('Lancez d\'abord un scan pour récupérer les articles');
      return;
    }
    
    setIsAnalyzing(true);
    addGlobalLog('ADVERSARY', 'Analyse IA propagande (Claude Sonnet) en cours...');
    
    try {
      const res = await axios.post(`${API}/news/adversary/analyze`, {
        origin: selectedOrigin,
        max_articles: 10
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      
      if (res.data.status === 'success') {
        setPropagandaResults(res.data);
        // Backward compat: also set narrativeAnalysis from global_assessment
        const ga = res.data.global_assessment || {};
        setNarrativeAnalysis({
          dominant_themes: ga.dominant_narratives || [],
          information_warfare_assessment: ga.information_warfare_assessment || '',
          avg_propaganda_score: ga.avg_propaganda_score,
        });
        addGlobalLog('ADVERSARY', `Score propagande moyen: ${ga.avg_propaganda_score?.toFixed(1) || 'N/A'}/10`);
        toast.success(`Analyse terminée — Score moyen: ${ga.avg_propaganda_score?.toFixed(1) || 'N/A'}/10`);
      } else if (res.data.status === 'warning') {
        toast.warning(res.data.message);
      } else {
        toast.error(res.data.message || 'Erreur analyse');
      }
    } catch (err) {
      console.error('Error analyzing narratives:', err);
      toast.error('Erreur lors de l\'analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateAdversaryReport = async () => {
    if (checkDemoBlock('Génération rapport adversaires')) return;
    
    setIsGeneratingReport(true);
    addGlobalLog('ADVERSARY', 'Génération du rapport PDF adversaires avec analyse IA...');
    toast.info('Génération du rapport en cours...');
    
    try {
      // Build query params - v2.6.0 uses query params instead of body
      const params = new URLSearchParams();
      if (selectedOrigin) params.append('origin', selectedOrigin);
      params.append('include_ai_analysis', 'true');
      
      const res = await axios.post(
        `${API}/reports/adversary?${params.toString()}`,
        {},  // Empty body - params are in query string
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',  // Expect PDF blob
          timeout: 90000 // 90 seconds timeout for AI analysis
        }
      );
      
      // Check if response is PDF or JSON
      const contentType = res.headers['content-type'];
      
      if (contentType && contentType.includes('application/pdf')) {
        // Download PDF
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `rapport_adversaires_${new Date().toISOString().slice(0,10)}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        addGlobalLog('ADVERSARY', 'Rapport PDF téléchargé avec succès');
        toast.success('Rapport adversaires téléchargé !');
        loadReportHistory();
      } else {
        // JSON response (fallback)
        const text = await res.data.text();
        const data = JSON.parse(text);
        
        if (data.status === 'success') {
          addGlobalLog('ADVERSARY', `Rapport JSON généré: ${data.articles_count} articles, analyse IA: ${data.ai_analysis ? 'OUI' : 'NON'}`);
          toast.success('Rapport adversaires généré (JSON) !');
          loadReportHistory();
        }
      }
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error('Erreur lors de la génération du rapport');
      addGlobalLog('ERROR', `Échec génération rapport adversaires: ${err.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-black/60 border-2 border-red-500/50 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-900/50 rounded">
              <Skull className="w-7 h-7 text-red-400" />
            </div>
            <div>
              <h2 className="font-heading text-red-400 text-2xl uppercase tracking-wider">
                Veille Adversaires
              </h2>
              <p className="font-mono text-white/80 text-sm">
                Sources d'État: Russie, Chine, Iran
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowSources(!showSources)}
              className="px-3 py-2 bg-marine-slate/30 border border-marine-slate/50 text-white/90 font-mono text-sm flex items-center gap-2 hover:bg-marine-slate/50"
              data-testid="toggle-sources-btn"
            >
              <Radio className="w-4 h-4" />
              Sources ({sources.length})
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-3 py-2 bg-purple-900/30 border border-purple-500/50 text-purple-400 font-mono text-sm flex items-center gap-2 hover:bg-purple-900/50"
              data-testid="toggle-history-btn"
            >
              <History className="w-4 h-4" />
              Historique ({reportHistory.length})
            </button>
            <button
              onClick={generateAdversaryReport}
              disabled={isGeneratingReport}
              className="px-4 py-2 bg-orange-900/50 border border-orange-500/50 text-orange-400 font-mono text-sm flex items-center gap-2 hover:bg-orange-900/70 disabled:opacity-50"
              data-testid="generate-adversary-report-btn"
            >
              {isGeneratingReport ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              RAPPORT PDF
            </button>
            <button
              onClick={fetchAdversaryNews}
              disabled={isLoading}
              className="px-4 py-2 bg-red-900/50 border border-red-500/50 text-red-400 font-mono text-sm flex items-center gap-2 hover:bg-red-900/70 disabled:opacity-50"
              data-testid="scan-adversary-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              SCANNER
            </button>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-500/50 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="font-mono text-sm text-yellow-200">
            <strong>AVERTISSEMENT:</strong> Ces données proviennent de médias d'État et de propagande. 
            L'objectif est de comprendre le narratif adverse, non d'obtenir des informations fiables.
          </div>
        </div>
      </div>

      {/* Sources Panel (collapsible) */}
      <AnimatePresence>
        {showSources && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-black/40 border border-marine-slate/30 p-4"
          >
            <h3 className="font-mono text-marine-cyan text-lg mb-4 flex items-center gap-2">
              <Radio className="w-5 h-5" />
              Sources Adversaires Configurées
            </h3>
            <div className="grid md:grid-cols-4 gap-4">
              {Object.entries(ORIGIN_CONFIG).map(([origin, config]) => {
                const originSources = sources.filter(s => s.origin === origin);
                return (
                  <div key={origin} className={`p-3 border ${config.borderColor} ${config.bgColor}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{config.flag}</span>
                      <span className={`font-mono font-bold ${config.color}`}>{config.label}</span>
                      <span className="font-mono text-white/80 text-sm ml-auto">({originSources.length})</span>
                    </div>
                    <div className="space-y-1">
                      {originSources.map(src => (
                        <div key={src.id} className="font-mono text-xs text-white/90 truncate">
                          • {src.name}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report History Panel (collapsible) */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-black/40 border border-purple-500/30 p-4"
          >
            <h3 className="font-mono text-purple-400 text-lg mb-4 flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique des Rapports Adversaires
            </h3>
            {reportHistory.length === 0 ? (
              <p className="font-mono text-white/80 text-sm">Aucun rapport généré</p>
            ) : (
              <div className="space-y-2">
                {reportHistory.map((report, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-purple-900/20 border border-purple-500/20 rounded"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-purple-400" />
                      <div>
                        <p className="font-mono text-sm text-white">
                          {report.filename}
                        </p>
                        <p className="font-mono text-xs text-white/80">
                          {new Date(report.timestamp).toLocaleString('fr-FR')} • 
                          {report.articles_count} articles • 
                          Par: {report.generated_by_name || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <a
                      href={`${API}/static/${report.filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-purple-900/50 border border-purple-500/50 text-purple-400 font-mono text-xs flex items-center gap-2 hover:bg-purple-900/70 rounded"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </a>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Origin Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-white/80 text-sm">Filtrer par origine:</span>
        <button
          onClick={() => setSelectedOrigin(null)}
          className={`px-3 py-1 font-mono text-sm border ${
            selectedOrigin === null 
              ? 'bg-marine-cyan/20 border-marine-cyan text-marine-cyan' 
              : 'border-marine-slate/50 text-white/90 hover:bg-marine-slate/30'
          }`}
          data-testid="filter-all"
        >
          TOUS
        </button>
        {Object.entries(ORIGIN_CONFIG).map(([origin, config]) => (
          <button
            key={origin}
            onClick={() => setSelectedOrigin(origin)}
            className={`px-3 py-1 font-mono text-sm border flex items-center gap-2 ${
              selectedOrigin === origin 
                ? `${config.bgColor} ${config.borderColor} ${config.color}` 
                : 'border-marine-slate/50 text-white/90 hover:bg-marine-slate/30'
            }`}
            data-testid={`filter-${origin}`}
          >
            <span>{config.flag}</span>
            {config.label}
            {byOrigin[origin] > 0 && (
              <span className="text-xs opacity-70">({byOrigin[origin]})</span>
            )}
          </button>
        ))}
      </div>

      {/* Articles List */}
      {articles.length > 0 && (
        <div className="bg-black/40 border border-marine-slate/30 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-mono text-marine-cyan text-lg flex items-center gap-2">
              <Newspaper className="w-5 h-5" />
              Articles Adversaires ({articles.length})
            </h3>
            <button
              onClick={analyzeNarratives}
              disabled={isAnalyzing}
              className="px-4 py-2 bg-purple-900/50 border border-purple-500/50 text-purple-400 font-mono text-sm flex items-center gap-2 hover:bg-purple-900/70 disabled:opacity-50"
              data-testid="analyze-narratives-btn"
            >
              {isAnalyzing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Target className="w-4 h-4" />
              )}
              SCORE PROPAGANDE (IA)
            </button>
          </div>

          {/* Propaganda Analysis Results */}
          {propagandaResults && propagandaResults.analyses?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 space-y-3"
            >
              {/* Global Assessment */}
              {propagandaResults.global_assessment && (
                <div className="p-4 bg-purple-900/20 border border-purple-500/30">
                  <h4 className="font-mono text-purple-400 font-bold mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Evaluation Globale — Score Propagande
                    <span className={`ml-auto px-3 py-1 rounded font-bold text-sm ${
                      propagandaResults.global_assessment.avg_propaganda_score >= 7 ? 'bg-red-900/50 text-red-300' :
                      propagandaResults.global_assessment.avg_propaganda_score >= 4 ? 'bg-orange-900/50 text-orange-300' :
                      'bg-green-900/50 text-green-300'
                    }`}>
                      {propagandaResults.global_assessment.avg_propaganda_score?.toFixed(1)}/10
                    </span>
                  </h4>
                  <p className="font-mono text-white/80 text-xs mb-2">
                    {propagandaResults.global_assessment.information_warfare_assessment}
                  </p>
                  {propagandaResults.global_assessment.dominant_narratives?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      <span className="font-mono text-white/50 text-[10px]">Narratifs dominants:</span>
                      {propagandaResults.global_assessment.dominant_narratives.map((n, i) => (
                        <span key={i} className="px-2 py-0.5 bg-purple-900/40 text-purple-300 font-mono text-[10px] rounded">
                          {n}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="font-mono text-white/30 text-[9px] mt-2">
                    {propagandaResults.source_type === 'LIVE_AI_ANALYSIS' ? 'ANALYSE IA LIVE (Claude Sonnet)' : propagandaResults.source_type}
                    {' — '}{propagandaResults.warning}
                  </p>
                </div>
              )}

              {/* Individual Article Scores */}
              <div className="grid gap-2 max-h-[300px] overflow-y-auto">
                {propagandaResults.analyses.map((a, i) => (
                  <div key={i} className={`p-3 border rounded ${
                    a.propaganda_score >= 7 ? 'border-red-500/40 bg-red-900/10' :
                    a.propaganda_score >= 4 ? 'border-orange-500/40 bg-orange-900/10' :
                    'border-green-500/40 bg-green-900/10'
                  }`} data-testid={`propaganda-score-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded ${
                            a.propaganda_score >= 7 ? 'bg-red-500/30 text-red-300' :
                            a.propaganda_score >= 4 ? 'bg-orange-500/30 text-orange-300' :
                            'bg-green-500/30 text-green-300'
                          }`}>
                            {a.propaganda_score}/10
                          </span>
                          <span className="font-mono text-white/50 text-[10px]">{a.propaganda_level}</span>
                          <span className="font-mono text-white/40 text-[10px]">{a.article_source} ({a.article_origin})</span>
                        </div>
                        <p className="font-mono text-white/90 text-xs line-clamp-1">{a.article_title}</p>
                        <p className="font-mono text-white/60 text-[10px] mt-1">{a.key_narrative}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {a.biases?.map((b, j) => (
                        <span key={j} className="px-1.5 py-0.5 bg-white/5 text-red-300/70 font-mono text-[9px] rounded border border-red-500/20">
                          {b}
                        </span>
                      ))}
                    </div>
                    {a.reliability && (
                      <span className={`mt-1 inline-block font-mono text-[9px] px-1.5 py-0.5 rounded ${
                        a.reliability === 'verified' ? 'bg-green-500/20 text-green-300' :
                        a.reliability === 'partially_true' ? 'bg-yellow-500/20 text-yellow-300' :
                        a.reliability === 'misleading' ? 'bg-orange-500/20 text-orange-300' :
                        a.reliability === 'false' ? 'bg-red-500/20 text-red-300' :
                        'bg-white/10 text-white/50'
                      }`}>
                        Fiabilite: {a.reliability}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Legacy Narrative Analysis (fallback) */}
          {narrativeAnalysis && !propagandaResults && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 bg-purple-900/20 border border-purple-500/30"
            >
              <h4 className="font-mono text-purple-400 font-bold mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Analyse des Narratifs Adversaires
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="font-mono text-white/80 text-xs mb-1">Themes dominants:</p>
                  <div className="flex flex-wrap gap-1">
                    {narrativeAnalysis.dominant_themes?.slice(0, 6).map((theme, i) => (
                      <span key={i} className="px-2 py-0.5 bg-purple-900/40 text-purple-300 font-mono text-xs rounded">
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Articles */}
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {articles
              .filter(a => !selectedOrigin || sources.find(s => s.name === a.source)?.origin === selectedOrigin)
              .map((article, idx) => {
                const sourceInfo = sources.find(s => s.name === article.source);
                const originConfig = ORIGIN_CONFIG[sourceInfo?.origin] || ORIGIN_CONFIG.russia;
                const isExpanded = expandedArticle === idx;
                
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`p-3 border ${originConfig.borderColor} ${originConfig.bgColor} cursor-pointer`}
                    onClick={() => setExpandedArticle(isExpanded ? null : idx)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm">{originConfig.flag}</span>
                          <span className={`font-mono text-xs ${originConfig.color}`}>
                            {article.source}
                          </span>
                          <span className="font-mono text-xs text-white/40">
                            {new Date(article.published).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <h4 className="font-mono text-sm text-white font-medium line-clamp-2">
                          {article.title}
                        </h4>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-2"
                          >
                            <p className="font-mono text-xs text-white/90 mb-2">
                              {article.summary}
                            </p>
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-marine-cyan text-xs font-mono hover:underline"
                              onClick={e => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                              Voir l'article original
                            </a>
                          </motion.div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-white/80" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-white/80" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {articles.length === 0 && !isLoading && (
        <div className="bg-black/40 border border-marine-slate/30 p-8 text-center">
          <Skull className="w-12 h-12 text-red-400/50 mx-auto mb-3" />
          <p className="font-mono text-white/80">
            Cliquez sur "SCANNER" pour récupérer les articles des sources adversaires
          </p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="bg-black/40 border border-marine-slate/30 p-8 text-center">
          <Loader2 className="w-12 h-12 text-red-400 mx-auto mb-3 animate-spin" />
          <p className="font-mono text-white/80 animate-pulse">
            Scan des sources adversaires en cours...
          </p>
        </div>
      )}
    </div>
  );
};

export default AdversaryIntelPanel;
